import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpEvent, HttpInterceptorFn, HttpRequest, HttpHandler, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { AuthService } from './services/auth.service';
import { IJwtToken } from './models/authenticate';
import { AppMessageService } from './services/app-message.service';
import { CommonService } from './services/common.service';
import { DeviceService } from './services/device.service';

// One in-flight refresh shared by every 401'd request. finalize() clears it on success, error AND
// unsubscribe, so the latch can never strand; shareReplay hands late subscribers the fresh token
// rather than a stale one, and a refresh failure propagates to all of them instead of hanging.
let refresh$: Observable<string> | null = null;

const sharedRefresh = (authService: AuthService): Observable<string> =>
  (refresh$ ??= authService.refreshToken().pipe(
    map((token: IJwtToken) => {
      if (!token.accessToken) {
        throw new Error('Missing access token');
      }
      return token.accessToken;
    }),
    finalize(() => (refresh$ = null)),
    shareReplay({ bufferSize: 1, refCount: false })
  ));

export const addToken = (req: HttpRequest<unknown>, token: string): HttpRequest<unknown> => {
  return req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) });
};

export const AuthInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const appMessageService = inject(AppMessageService);
  const commonService = inject(CommonService);
  const deviceService = inject(DeviceService);
  const router = inject(Router);
  const token = authService.getJwtTokenString();

  const withToken = token ? addToken(req, token) : req;
  const authReq = withToken.clone({ headers: withToken.headers.set('X-Device-Id', deviceService.getDeviceId()) });

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.url?.includes('PushDevice/') || error.url?.includes('/hubs/')) {
        return throwError(() => error);
      }

      // Do not retry for refresh endpoint or network errors
      if (error.status === 0 || error.url?.includes('User/refresh-token')) {
        if (authService.isAuthenticated()) {
          authService.logout();
        }
        return throwError(() => error);
      }

      // Token expired. A role denial is a 403 (VMS.API AuthorizeAttribute), never a 401, so a 401
      // here always means the access token itself needs refreshing. The retry goes straight to
      // `next`, so it cannot re-enter this interceptor and loop.
      if (error.status === 401) {
        if (!authService.getJwtToken()?.refreshToken) {
          authService.logout();
          return throwError(() => error);
        }

        return sharedRefresh(authService).pipe(
          // Only a failed refresh ends the session - errors from the retried request itself must
          // fall through to the caller untouched.
          catchError(err => {
            authService.logout();
            return throwError(() => err);
          }),
          switchMap(accessToken => next(addToken(authReq, accessToken)))
        );
      }

      if (error.status === 403) {
        const body = error.error ?? {};
        const code = body.code ?? body.Code;
        const subscriptionMessage = body.message ?? body.ErrorMessage ?? body.errorMessage;
        if (code === 'SUBSCRIPTION_READONLY') {
          if (commonService.clientSubscriptionStatus) {
            commonService.clientSubscriptionStatus = {
              ...commonService.clientSubscriptionStatus,
              accessMode: 'ReadOnly',
              canWrite: false,
              isLocked: false
            };
            commonService.syncReadOnlyUiState();
          }
          appMessageService.warn(subscriptionMessage ?? 'Subscription expired. Read-only mode is active.');
        } else if (code === 'SUBSCRIPTION_LOCKED') {
          if (commonService.clientSubscriptionStatus) {
            commonService.clientSubscriptionStatus = {
              ...commonService.clientSubscriptionStatus,
              accessMode: 'Locked',
              canWrite: false,
              isLocked: true
            };
          } else {
            commonService.clientSubscriptionStatus = {
              subscriptionStartUtc: '',
              subscriptionEndUtc: '',
              graceEndUtc: '',
              accessMode: 'Locked',
              daysRemaining: 0,
              canWrite: false,
              isLocked: true
            };
          }
          if (authService.isAuthenticated()) {
            router.navigate(['/subscription-locked']);
          }
        }
      }

      return throwError(() => error);
    })
  );
};
